/**
 * Member-origin person properties.
 *
 * A member migrated from Pelago 1.0 keeps the same id in both analytics
 * projects, so the person already exists; what it lacks is any record of the
 * migration. This pure derivation is the single owner of the three facts
 * downstream questions key on — whether the member came from legacy, when
 * they migrated, and who they were in the legacy project — so every trigger
 * that syncs person properties (sign-up, migration, sweep) sends the same
 * values from the same inputs.
 */

import { DateTime } from 'luxon';

/** Person-property keys as they appear in the analytics store. */
export const MEMBER_ORIGIN_PERSON_PROPERTY_KEYS = {
  memberOrigin: 'member_origin',
  migratedAt: 'migrated_at',
  legacyDistinctId: 'legacy_distinct_id',
} as const;

/** Closed vocabulary: where the member record originated. */
export const MEMBER_ORIGINS = ['legacy', 'galapagos'] as const;
export type MemberOrigin = (typeof MEMBER_ORIGINS)[number];

export interface MemberOriginInput {
  /** The member's canonical id — for a migrated member, the legacy identity id. */
  readonly memberId: string;
  /** The legacy Cognito sub recorded at migration, or null when resolved by email only. */
  readonly legacyBackend2Sub: string | null;
  /**
   * The member's migration marker, or null for a natively registered member.
   * A marker exists from the moment a legacy member signs in; completion is
   * stamped only once the Tier 0 transfer commits.
   */
  readonly migration: { readonly tier0CompletedAt: Date | null } | null;
}

export interface MemberOriginProperties {
  readonly [MEMBER_ORIGIN_PERSON_PROPERTY_KEYS.memberOrigin]: MemberOrigin;
  readonly [MEMBER_ORIGIN_PERSON_PROPERTY_KEYS.legacyDistinctId]?: string;
  readonly [MEMBER_ORIGIN_PERSON_PROPERTY_KEYS.migratedAt]?: string;
}

/**
 * Derive the origin properties for one member.
 *
 * - No marker → `member_origin: 'galapagos'` and nothing else.
 * - Marker without completion → `member_origin: 'legacy'` + `legacy_distinct_id`.
 * - Completed marker → the above plus `migrated_at` (ISO-8601, UTC).
 *
 * A key whose source is absent is omitted, never written null, so a later
 * send can only add facts. `legacy_distinct_id` falls back to the member id
 * because a migrated member's id IS the legacy identity id by construction.
 */
export function deriveMemberOriginProperties(input: MemberOriginInput): MemberOriginProperties {
  if (input.migration === null) {
    return { [MEMBER_ORIGIN_PERSON_PROPERTY_KEYS.memberOrigin]: 'galapagos' };
  }
  const properties: MemberOriginProperties = {
    [MEMBER_ORIGIN_PERSON_PROPERTY_KEYS.memberOrigin]: 'legacy',
    [MEMBER_ORIGIN_PERSON_PROPERTY_KEYS.legacyDistinctId]:
      input.legacyBackend2Sub ?? input.memberId,
  };
  if (input.migration.tier0CompletedAt === null) return properties;
  // An unparseable completion instant is a corrupt marker, not "not yet
  // completed": surface it rather than silently writing a partial record.
  const completedAt = DateTime.fromJSDate(input.migration.tier0CompletedAt, { zone: 'utc' });
  const migratedAt = completedAt.isValid ? completedAt.toISO() : null;
  if (migratedAt === null) {
    throw new RangeError(
      `migration completion is not a valid instant: ${completedAt.invalidReason ?? 'unknown'}`,
    );
  }
  return { ...properties, [MEMBER_ORIGIN_PERSON_PROPERTY_KEYS.migratedAt]: migratedAt };
}
